<template>
  <div
    class="artifact-list-wrapper"
    :style="wrapperStyle"
    @mouseenter="isMouseInside = true"
    @mouseleave="isMouseInside = false"
    @dblclick.stop
  >
    <div
      ref="scrollContainer"
      class="artifact-list-scroller"
      :class="{ 'show-scrollbar': showScrollbar }"
      @scroll="onScroll"
    >
      <div
        v-for="{ artifact, leftDots, rightDots } in visibleArtifactsWithDots"
        :key="artifact.id"
        class="artifact-item"
        :class="{
          selected: selectedArtifactIdAsNumber === artifact.id,
          linked: linkedArtifactIds.has(artifact.id),
          fixed: fixedArtifactId === artifact.id,
        }"
        :data-artifact-id="artifact.id"
        @click="onArtifactClick(artifact)"
        @dblclick.stop="onArtifactDblClick(artifact)"
      >
        <div class="left-dots" @click.stop="onDotsClick($event, artifact, 'incoming')">
          <span
            v-for="(dot, index) in leftDots.visible"
            :key="'left-' + index"
            class="dot"
            :style="{ backgroundColor: dot.color }"
          ></span>
          <span v-if="leftDots.overflow > 0" class="dot-overflow-wrapper">
            <span class="dot-overflow">+{{ leftDots.overflow }}</span>
            <div class="dot-tooltip">
              <div v-for="(item, i) in leftDots.all" :key="'lt-' + i" class="tooltip-item">
                <span class="tooltip-dot" :style="{ backgroundColor: item.color }"></span>
                <span class="tooltip-label">{{ item.name }} ({{ item.count }})</span>
              </div>
            </div>
          </span>
        </div>
        <div class="artifact-name" :title="artifact.name">
          {{ artifact.name }}
        </div>
        <div class="right-dots" @click.stop="onDotsClick($event, artifact, 'outgoing')">
          <span
            v-for="(dot, index) in rightDots.visible"
            :key="'right-' + index"
            class="dot"
            :style="{ backgroundColor: dot.color }"
          ></span>
          <span v-if="rightDots.overflow > 0" class="dot-overflow-wrapper">
            <span class="dot-overflow">+{{ rightDots.overflow }}</span>
            <div class="dot-tooltip dot-tooltip--right">
              <div v-for="(item, i) in rightDots.all" :key="'rt-' + i" class="tooltip-item">
                <span class="tooltip-dot" :style="{ backgroundColor: item.color }"></span>
                <span class="tooltip-label">{{ item.name }} ({{ item.count }})</span>
              </div>
            </div>
          </span>
        </div>
      </div>
      <div v-if="isLoading" class="loading-indicator">Loading...</div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Artifact, ArtifactType } from '@/models'
  import { colorFromApiColor } from '@/pages/explorer/rappid/rappid-helpers'
  import { useArtifactCacheStore } from '@/stores/artifactCacheStore'
  import { useLinkCacheStore } from '@/stores/linkCacheStore'
  import { useExplorerUiStore } from '@/stores/explorerUiStore'
  import { useLinkedTypes } from '@/composables/useLinkedTypes'
  import { storeToRefs } from 'pinia'
  import { computed, onMounted, ref, watch, nextTick } from 'vue'

  interface DotInfo {
    color: string
    name: string
    count: number
  }

  interface DotsResult {
    visible: DotInfo[]
    overflow: number
    all: DotInfo[]
  }

  const props = defineProps({
    artifactTypeId: { type: Number, required: true },
  })

  const scrollContainer = ref<HTMLElement | null>(null)
  const isLoading = ref(false)
  const isMouseInside = ref(false)

  const artifactCacheStore = useArtifactCacheStore()
  const linkCacheStore = useLinkCacheStore()
  const explorerUiStore = useExplorerUiStore()
  const { selectedArtifactId } = storeToRefs(explorerUiStore)

  // The store stores artifact ID as string, but we need number for comparisons and composable
  const selectedArtifactIdAsNumber = computed<number | undefined>(() =>
    selectedArtifactId.value ? parseInt(selectedArtifactId.value) : undefined,
  )

  const { incomingTypes, outgoingTypes } = useLinkedTypes(selectedArtifactIdAsNumber)

  const fixedArtifactId = computed<number | null>(() => {
    const storeFixedId = explorerUiStore.fixedArtifactId
    return storeFixedId ? parseInt(storeFixedId) : null
  })

  const artifactType = computed<ArtifactType | undefined>(() =>
    artifactCacheStore.artifactTypes.find((at) => at.id === props.artifactTypeId),
  )

  const visibleArtifacts = computed<Artifact[]>(() => {
    return (artifactCacheStore.artifactsByType.get(props.artifactTypeId) ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
  })

  const linkedArtifactIds = computed<Set<number>>(() => explorerUiStore.linkedArtifactIds)

  const wrapperStyle = computed(() => {
    const at = artifactType.value
    if (!at) return {}
    const borderColor = colorFromApiColor(at.color)
    return {
      '--border-color': borderColor,
    }
  })

  // Show scrollbar only when mouse is inside AND there are 10+ artifacts
  const showScrollbar = computed(() => {
    return isMouseInside.value && visibleArtifacts.value.length >= 10
  })

  const MAX_DOTS = 3

  function computeLeftDots(artifact: Artifact): DotsResult {
    const typeMap = new Map<number, DotInfo>()

    for (const link of linkCacheStore.linksWithB.get(artifact.id) ?? []) {
      const sourceArtifact = artifactCacheStore.artifactsById.get(link.artifactAId)
      if (sourceArtifact) {
        const typeId = sourceArtifact.artifactType.id
        const existing = typeMap.get(typeId)
        if (existing) {
          existing.count++
        } else {
          const color = sourceArtifact.artifactType.color
            ? colorFromApiColor(sourceArtifact.artifactType.color)
            : '#666666'
          typeMap.set(typeId, { color, name: sourceArtifact.artifactType.name, count: 1 })
        }
      }
    }

    const all = Array.from(typeMap.values())
    return {
      visible: all.slice(0, MAX_DOTS),
      overflow: Math.max(0, all.length - MAX_DOTS),
      all,
    }
  }

  function computeRightDots(artifact: Artifact): DotsResult {
    const typeMap = new Map<number, DotInfo>()

    for (const link of linkCacheStore.linksWithA.get(artifact.id) ?? []) {
      const targetArtifact = artifactCacheStore.artifactsById.get(link.artifactBId)
      if (targetArtifact) {
        const typeId = targetArtifact.artifactType.id
        const existing = typeMap.get(typeId)
        if (existing) {
          existing.count++
        } else {
          const color = targetArtifact.artifactType.color
            ? colorFromApiColor(targetArtifact.artifactType.color)
            : '#666666'
          typeMap.set(typeId, { color, name: targetArtifact.artifactType.name, count: 1 })
        }
      }
    }

    const all = Array.from(typeMap.values())
    return {
      visible: all.slice(0, MAX_DOTS),
      overflow: Math.max(0, all.length - MAX_DOTS),
      all,
    }
  }

  const visibleArtifactsWithDots = computed(() => {
    return visibleArtifacts.value.map((artifact) => ({
      artifact,
      leftDots: computeLeftDots(artifact),
      rightDots: computeRightDots(artifact),
    }))
  })

  function onArtifactClick(artifact: Artifact) {
    explorerUiStore.setSelectedArtifact(artifact.id.toString())
    explorerUiStore.triggerArtifactClick(artifact)
  }

  function onArtifactDblClick(artifact: Artifact) {
    explorerUiStore.triggerArtifactDblClick(artifact)
  }

  function onDotsClick(event: MouseEvent, artifact: Artifact, direction: 'incoming' | 'outgoing') {
    // Select the artifact (this updates selectedArtifactId reactively via storeToRefs)
    explorerUiStore.setSelectedArtifact(artifact.id.toString())

    // Use composable's computed linked types (now based on store's selectedArtifactId)
    const linkedTypes = direction === 'incoming' ? incomingTypes.value : outgoingTypes.value

    // Only open popup if there are linked types
    if (linkedTypes.length > 0) {
      const position = { x: event.clientX, y: event.clientY }
      explorerUiStore.openLinkedTypesPopup(artifact, direction, linkedTypes, position)
    }
  }

  function onScroll() {
    if (!scrollContainer.value || isLoading.value) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value
    const scrollThreshold = 50

    // Only trigger if there's actual scrollable content (prevents immediate trigger on expand)
    // and user has actually scrolled near the bottom
    if (scrollHeight <= clientHeight) return

    if (scrollTop + clientHeight >= scrollHeight - scrollThreshold) {
      loadMoreArtifacts()
    }
  }

  async function loadMoreArtifacts() {
    const at = artifactType.value
    if (!at || isLoading.value) return

    const totalCount = at.artifactCount ?? 0
    const loadedCount = visibleArtifacts.value.length

    if (loadedCount >= totalCount) return

    isLoading.value = true
    explorerUiStore.triggerLoadMoreArtifacts(at)
  }

  // Watch for loading completion from store
  watch(
    () => visibleArtifacts.value.length,
    () => {
      isLoading.value = false
    },
  )

  // Watch for external selection changes to scroll into view
  watch(
    () => explorerUiStore.selectedArtifactId,
    (newId) => {
      if (newId && scrollContainer.value) {
        nextTick(() => {
          const element = scrollContainer.value?.querySelector(`[data-artifact-id="${newId}"]`)
          element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
      }
    },
  )

  onMounted(() => {
    // Scroll to initially selected artifact if any
    const storeId = explorerUiStore.selectedArtifactId
    if (storeId && scrollContainer.value) {
      nextTick(() => {
        const element = scrollContainer.value?.querySelector(`[data-artifact-id="${storeId}"]`)
        element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    }
  })
</script>

<style scoped lang="scss">
  .artifact-list-wrapper {
    height: 100%;
    width: 100%;
    padding: 16px 10px 6px 10px;
    box-sizing: border-box;
  }

  .artifact-list-scroller {
    height: 100%;
    overflow-y: scroll; // Always reserves scrollbar gutter — prevents layout shift on show/hide
    overflow-x: hidden;

    &::-webkit-scrollbar {
      width: 8px;
    }

    // Default: invisible scrollbar (thumb and track transparent)
    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 4px;
    }

    // Firefox default: invisible
    scrollbar-color: transparent transparent;

    // Visible state (mouse inside + 10+ items)
    &.show-scrollbar {
      &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.3);

        &:hover {
          background: rgba(0, 0, 0, 0.5);
        }
      }

      // Firefox
      scrollbar-color: rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.1);
    }
  }

  .artifact-item {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0;
    cursor: pointer;
    border-radius: 20px;
    border: 1.5px solid transparent;
    margin-bottom: 8px;
    background: transparent;
    transition:
      background-color 0.15s ease,
      border-color 0.15s ease;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    &.selected {
      background-color: rgba(0, 0, 0, 0.1);
      border-color: transparent;
    }

    &.linked {
      border-color: rgba(0, 0, 0, 0.22);
    }

    &.selected.linked {
      background-color: rgba(0, 0, 0, 0.1);
      border-color: transparent;
    }

    &.fixed {
      background-color: rgba(25, 118, 210, 0.15);
      border-color: #1976d2;
    }
  }

  .left-dots,
  .right-dots {
    width: 40px;
    min-width: 40px;
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 3px;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .dot-overflow-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    &:hover .dot-tooltip {
      display: block;
    }
  }

  .dot-overflow {
    font-family: 'Roboto', sans-serif;
    font-size: 10px;
    font-weight: 500;
    color: #666;
    cursor: default;
    white-space: nowrap;
    line-height: 1;
  }

  .dot-tooltip {
    display: none;
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    padding: 6px 10px;
    min-width: 180px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1000;

    &--right {
      left: auto;
      right: 50%;
      transform: translateX(50%);
    }
  }

  .tooltip-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 0;
    white-space: nowrap;
  }

  .tooltip-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .tooltip-label {
    font-family: 'Roboto', sans-serif;
    font-size: 12px;
    color: #333;
  }

  .artifact-name {
    flex: 1;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    color: #212121;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 5px;
  }

  .loading-indicator {
    text-align: center;
    padding: 10px;
    font-family: 'Roboto', sans-serif;
    font-size: 12px;
    color: #666;
  }
</style>
